import { useEffect, useMemo, useRef, useState } from 'react'
import { uniqBy } from 'lodash'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { graphql } from 'app/generated/gql'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { notifyError } from 'app/services/notify.client'
import { setOrg, setWorkspace, useUser, useWorkspace } from 'app/utils'
import { useAuthStore } from 'app/stores/StoreContext'
import { debug } from 'lib/logging'
import type { User } from 'app/types/entities'
import { Jsonify } from 'lib/toJSON'
import { OptionType } from 'types/OptionType'
import { buildWorkspaceSearch } from './workspaceSearch'

const OPS_WORKSPACE_SEARCH_QUERY = graphql(`
  query OpsWorkspaceSearch($query: String!) {
    opsWorkspaceSearch(query: $query) {
      workspaceId
      workspaceName
      orgId
      locationId
      locationName
    }
  }
`)

type OpsWorkspaceSearchResult = {
  workspaceId: string
  workspaceName: string
  orgId: string
  locationId?: string | null
  locationName?: string | null
}

const ORG_OPTIONS: OptionType[] = [
  { name: `SMC`, id: `5044d341-1053-4ffe-b6ad-5924a0dc5b60`, value: `5044d341-1053-4ffe-b6ad-5924a0dc5b60` },
  { name: `Trackable`, id: `00000000-0000-0000-0000-000000000000`, value: `00000000-0000-0000-0000-000000000000` },
]

export type UseGlobalWorkspaceSelectorReturn = {
  me: Jsonify<User> | undefined
  isOpen: boolean
  setOpen: (open: boolean) => void
  canPick: boolean
  isOrgUser: boolean
  selectedWorkspace: OptionType | undefined
  workspaces: OptionType[]
  searchValue: string
  loading: boolean
  initialLoading: boolean
  orgOptions: OptionType[]
  inputRef: React.RefObject<HTMLInputElement | null>
  onWorkspaceChange: (workspace: OptionType) => void
  onSearchValueChange: (value: string) => void
  onClearSearch: () => void
  onOrgChange: (org: OptionType) => void
}

export function useGlobalWorkspaceSelector(): UseGlobalWorkspaceSelectorReturn {
  const me = useUser()
  const workspace = useWorkspace()
  const auth = useAuthStore()
  const inputRef = useRef<HTMLInputElement>(null!)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedWorkspace, setSelectedWorkspace] = useState<OptionType | undefined>(undefined)
  const [searchValue, setSearchValue] = useState(``)
  const [workspaces, setWorkspaces] = useState<OptionType[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)

  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const isOrgUser = useIsOrgUser()
  const isWorkspaceUserWithStaffV2 = useMemo(() => !isOrgUser && staffV2Enabled, [isOrgUser, staffV2Enabled])

  const canPick = useMemo(
    () => isOrgUser || (isWorkspaceUserWithStaffV2 && (me?.workspaces?.length ?? 0) > 1),
    [me, isOrgUser, isWorkspaceUserWithStaffV2],
  )

  const fetchWorkspaces = (query?: string) => {
    if (me?.type === `super`) {
      fetchSuperWorkspaces(query)
      return
    }

    const { workspaceSearch, locationSearch } = buildWorkspaceSearch(me, query)

    // Always fetch all workspaces for the org; when there's a query also search locations by name
    const workspacesPromise = gqlFetchList<{ id: string; name: string }>(
      'workspaces',
      { search: workspaceSearch, pagination: { limit: 1000 } },
      'id name',
    )
    const locationsPromise = query
      ? gqlFetchList<{ id: string; name: string; workspaceId: string }>(
          'locations',
          { search: locationSearch, pagination: { limit: 50 } },
          'id name workspaceId',
        )
      : Promise.resolve({ hits: [] as { id: string; name: string; workspaceId: string }[] })

    Promise.all([workspacesPromise, locationsPromise])
      .then(([wsRes, locRes]) => {
        const locationWorkspaceIds = new Set(locRes.hits?.map((l) => l.workspaceId) ?? [])
        const lowerQuery = query?.toLowerCase()

        let data = uniqBy(
          (wsRes.hits ?? []).map((ws) => ({
            id: ws.id!,
            value: ws.id!,
            name: ws.name!,
            meta: { locationMatches: locationWorkspaceIds.has(ws.id!) },
          })),
          (i: OptionType) => i.id,
        )

        // Filter to workspaces matching name OR having a matching location
        if (lowerQuery) {
          data = data.filter(
            (ws) => ws.name.toLowerCase().includes(lowerQuery) || locationWorkspaceIds.has(ws.id as string),
          )
        }

        data = applyAssignedFilter(data)
        data.sort((a, b) => a.name.localeCompare(b.name))
        debug(`workspace`, `cleaned data`, data)
        setWorkspaces(data)
        setInitialLoading(false)
        setLoading(false)
      })
      .catch(() => {
        setInitialLoading(false)
        setLoading(false)
      })
  }

  const fetchSuperWorkspaces = (query?: string) => {
    // opsWorkspaceSearch takes no orgId argument and always answers across every org, so the
    // browse scope has to be applied here.
    const { orgScopeId } = buildWorkspaceSearch(me, query)
    opsClient
      .query(OPS_WORKSPACE_SEARCH_QUERY, { query: query ?? `` })
      .toPromise()
      .then((result) => {
        const data = (result.data?.opsWorkspaceSearch ?? []) as OpsWorkspaceSearchResult[]
        const scoped = orgScopeId ? data.filter((workspace) => workspace.orgId === orgScopeId) : data
        setWorkspaces(
          scoped.map((workspace) => ({
            id: workspace.workspaceId,
            value: workspace.workspaceId,
            name: workspace.workspaceName,
            meta: { locationMatches: Boolean(workspace.locationId), orgId: workspace.orgId },
          })),
        )
        setInitialLoading(false)
        setLoading(false)
      })
      .catch(() => {
        setInitialLoading(false)
        setLoading(false)
      })
  }

  const applyAssignedFilter = (data: OptionType[]): OptionType[] => {
    const assignedIds =
      me?.type === `super`
        ? undefined
        : isWorkspaceUserWithStaffV2
          ? me?.workspaces?.map((w) => w.id)
          : me?.assignedWorkspaceIds
    if (assignedIds?.length) {
      return data.filter((ws) => assignedIds.includes(ws.id))
    } else if (isWorkspaceUserWithStaffV2) {
      notifyError(`No workspaces assigned. Contact your administrator.`)
      return []
    }
    return data
  }

  // Only fetch workspaces after user data is loaded
  useEffect(() => {
    if (auth.loading || !me?.id) {
      // User data not loaded yet, don't fetch workspaces
      return
    }
    fetchWorkspaces()
  }, [auth.loading, me?.id])

  // Re-fetch when the popover opens to pick up workspace creates/deletes.
  // gqlFetchList is imperative (not a subscribed hook query), so graphcache
  // invalidation alone won't trigger a re-render here.
  useEffect(() => {
    if (isOpen && me?.id) {
      fetchWorkspaces(searchValue || undefined)
    }
  }, [isOpen])

  // Update selected workspace when workspace data is loaded
  useEffect(() => {
    if (workspace?.id) {
      setSelectedWorkspace({ id: workspace.id, name: workspace.name, value: workspace.id })
    }
  }, [workspace?.id])

  const onWorkspaceChange = async (ws: OptionType) => {
    setSelectedWorkspace(ws)
    setIsOpen(false)
    await setWorkspace(ws.id || null)
  }

  const onSearchValueChange = (value: string) => {
    setLoading(true)
    setSearchValue(value)
    fetchWorkspaces(value)
  }

  const onClearSearch = () => {
    onSearchValueChange(``)
    inputRef.current?.focus()
  }

  const onOrgChange = async (selectedOption: OptionType) => {
    debug(`org`, `changing org`, selectedOption.id)
    const selectedOrgId = selectedOption.id as string
    await setOrg(selectedOrgId, selectedOrgId ? `/companies/locations` : undefined)
  }

  return {
    me,
    isOpen,
    setOpen: setIsOpen,
    canPick,
    isOrgUser,
    selectedWorkspace,
    workspaces,
    searchValue,
    loading,
    initialLoading,
    orgOptions: ORG_OPTIONS,
    inputRef,
    onWorkspaceChange,
    onSearchValueChange,
    onClearSearch,
    onOrgChange,
  }
}
